import { useContextoAssistente } from "@/context/GavetaContext";
import VoltarLink from "@/components/VoltarLink";
import BannerComo from "@/components/BannerComo";
import { BadgeParticular, marcarParticular } from "@/components/Particular";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmBaixa } from "@/components/ConfirmBaixa";
import {
  ArrowLeft, Calendar, Clock, Building2, FileText, User, Phone, Mail,
  MessageCircle, Send, Check, CalendarPlus, Video, Pencil, Copy, X, Ban, RotateCcw, Users, MapPin, Share2, Lock, UserPlus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import MarcarFeito from "@/components/MarcarFeito";
import NewEventDialog, { EventTypeBadge } from "@/components/NewEventDialog";
import CompartilharPublicacao from "@/components/CompartilharPublicacao";
import ConvidarTestemunha from "@/components/ConvidarTestemunha";
import { formatDate, formatCourtDistrict, publicationDate, toTitleCasePt, brDatesInText } from "@/lib/format";

import { Dica } from "@/components/Dica";
import ProcessoLink from "@/components/ProcessoLink";
const PROVIDER_LABEL = {
  meet: "Google Meet", zoom: "Zoom", teams: "Microsoft Teams", webex: "Webex", outro: "Videoconferência",
};

// Mesmas tabelas usadas em HearingsList/NewEventDialog — fase só se aplica a audiência,
// área do processo define o rótulo das partes (Reclamante/Reclamada x Autor/Réu).
const PHASE_LABELS = { inicial: "Inicial", instrucao: "Instrução", julgamento: "Julgamento", outro: "Outro" };
const PARTY_LABELS = {
  trabalhista: ["Reclamante", "Reclamada"],
  civel: ["Autor", "Réu"],
  penal: ["Autor", "Réu"],
  outro: ["Autor", "Réu"],
};

const waLink = (phone, msg) => {
  const digits = (phone || "").replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
};

export default function HearingDetail() {
  const { id } = useParams();
  const { user } = useAuth() || {};
  const [pub, setPub] = useState(null);
  useContextoAssistente(pub ? { tipo: "audiencia", id: pub.pub_id || id, rotulo: pub.hearing?.parties || pub.hearing?.case_number || "compromisso" } : null);   // Lote 154
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState([]);
  const [waMsg, setWaMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [shareOpen, setShareOpen] = useState(false); // Lote 93: "Compartilhar" no topo
  const [tesOpen, setTesOpen] = useState(false);      // Lote 176: convite de testemunha
  const [tesAtual, setTesAtual] = useState(null);

  const load = async () => {
    const r = await http.get(`/publications/${id}`);
    setPub(r.data);
    if (r.data.client_id) {
      const c = await http.get(`/clients`);
      setClient(c.data.find((x) => x.client_id === r.data.client_id) || null);
    } else {
      setClient(null);
    }
    const n = await http.get(`/publications/${id}/notifications`);
    setHistory(n.data);
  };

  useEffect(() => { load(); }, [id]);

  // Lote 98: baixa do prazo direto na tela do prazo (1 toque), com "Desfazer" por 6 s.
  const [baixando, setBaixando] = useState(false);
  const setPrazoDone = async (done) => {
    if (done && !(await confirmBaixa(pub?.hearing?.parties || pub?.hearing?.case_number || ""))) return;
    setBaixando(true);
    try {
      const r = await http.post(`/publications/${id}/${done ? "mark-prazo-done" : "unmark-prazo-done"}`);
      setPub((cur) => ({ ...cur, prazo_done: !!r.data?.prazo_done, prazo_done_at: r.data?.prazo_done_at || null }));
      if (done) {
        toast.success("Prazo marcado como feito", {
          duration: 6000,
          action: { label: "Desfazer", onClick: async () => {
            try {
              const u = await http.post(`/publications/${id}/unmark-prazo-done`);
              setPub((cur) => ({ ...cur, prazo_done: !!u.data?.prazo_done, prazo_done_at: null }));
            } catch { toast.error("Não consegui desfazer"); }
          } },
        });
      } else {
        toast.success("Prazo reaberto");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar o prazo");
    } finally {
      setBaixando(false);
    }
  };

  // Lote 69: o botão "Delegar" do quadro de alertas abre aqui já na seção "Avisar equipe".
  const location = useLocation();
  useEffect(() => {
    if (pub && location.hash === "#avisar-equipe") {
      // Lote 94: o "Delegar" do Painel/Agenda cai direto na folha do Compartilhar (antes rolava até o formulário).
      if (pub.status !== "cancelled") setShareOpen(true);
    }
  }, [pub, location.hash]);

  useEffect(() => {
    if (pub && client) {
      const h = pub.hearing;
      // "Outro" (antigo "Reunião", Lote 56) vira "compromisso" nesse texto — nome genérico
      // que cobre perícia, diligência etc., sem forçar uma palavra específica. É masculino
      // ("o compromisso marcado"), diferente de "audiência" (feminino) — por isso o artigo/
      // concordância dependem do tipo, não é sempre "a ... marcada" como antes.
      const isOutro = h?.event_type === "reuniao";
      const isPrazo = h?.event_type === "prazo";
      const label = isOutro ? "compromisso" : isPrazo ? "prazo" : "audiência";
      const art = isOutro || isPrazo ? "o" : "a";
      const marcado = isOutro || isPrazo ? "marcado" : "marcada";
      if (pub.status === "cancelled") {
        let text = `Olá ${client.name}, aqui é do escritório. Informamos que ${art} ${label} ${marcado} para ${formatDate(h.date)} às ${h.time} foi cancelada.`;
        if (h.case_number) text += ` Processo: ${h.case_number}.`;
        if (pub.cancel_reason) text += `\nMotivo: ${pub.cancel_reason}`;
        text += "\n\nQualquer dúvida, fico à disposição.";
        setWaMsg(text);
        return;
      }
      let text = `Olá ${client.name}, aqui é do escritório. Lembrete de ${label} em ${formatDate(h.date)} às ${h.time}.`;
      if (h.court) text += ` ${h.court}.`;
      if (h.case_number) text += ` Processo: ${h.case_number}.`;
      if (h.is_virtual && h.meeting_url) {
        text += `\n\n🎥 Videoconferência (${PROVIDER_LABEL[h.meeting_provider] || "sala virtual"}): ${h.meeting_url}`;
        if (h.meeting_id) text += `\nID: ${h.meeting_id}`;
        if (h.meeting_password) text += `\nSenha: ${h.meeting_password}`;
      }
      const confirmUrl = `${window.location.origin}/confirmar/${pub.confirm_token}`;
      if (pub.confirm_token) {
        text += `\n\nClique e confirme sua presença (ou peça remarcação):\n${confirmUrl}`;
      }
      setWaMsg(text);
    }
  }, [pub, client]);

  const cancelHearing = async () => {
    setCancelling(true);
    try {
      await http.post(`/publications/${id}/cancel`, { reason: cancelReason.trim() || null });
      toast.success("Audiência cancelada", { description: "O evento foi removido do Google Calendar." });
      setCancelOpen(false);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar");
    } finally { setCancelling(false); }
  };

  const reactivateHearing = async () => {
    setReactivating(true);
    try {
      await http.post(`/publications/${id}/reactivate`);
      toast.success("Audiência reativada", { description: "O evento voltou para o Google Calendar." });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reativar");
    } finally { setReactivating(false); }
  };

  const sendEmail = async () => {
    setLoading(true);
    try {
      await http.post(`/publications/${id}/notify`, { channel: "email" });
      toast.success("E-mail enviado ao cliente");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar e-mail");
    } finally { setLoading(false); }
  };

  const logWhats = async () => {
    setLoading(true);
    try {
      await http.post(`/publications/${id}/notify`, { channel: "whatsapp", message: waMsg });
      toast.success("Aviso registrado no histórico");
      window.open(waLink(client.phone, waMsg), "_blank", "noopener");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  const copyConfirmLink = () => {
    const url = `${window.location.origin}/confirmar/${pub.confirm_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const copyTeamLink = () => {
    const url = `${window.location.origin}/aviso-equipe/${pub.team_share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  if (!pub) return <div className="p-12 text-stone-500">Carregando…</div>;
  const h = pub.hearing || {};
  const confirmUrl = pub.confirm_token ? `${window.location.origin}/confirmar/${pub.confirm_token}` : null;

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto">
      <BannerComo />
      <div className="flex items-center justify-between gap-2 flex-wrap mb-6">
        <VoltarLink to="/audiencias" data-testid="voltar-link" className="text-xs font-semibold text-stone-500 hover:text-stone-900 flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Voltar
        </VoltarLink>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.escritorio && !new URLSearchParams(location.search).get("de") && (
            pub.particular_ef && !pub.particular ? (
              <BadgeParticular testid="particular-via-processo" className="h-8 px-2.5 text-[11px]" />
            ) : (
              <Button
                data-testid="particular-toggle"
                size="sm"
                variant="outline"
                onClick={async () => {
                  const r = await marcarParticular("evento", [pub.pub_id], !pub.particular);
                  if (r?.alterados) setPub((p) => ({ ...p, particular: !p.particular, particular_ef: !p.particular }));
                }}
                className="gap-2 border-stone-300"
                title={pub.particular ? "O escritório volta a ver este evento" : "O escritório deixa de ver este evento"}
              >
                <Lock className="w-3.5 h-3.5" strokeWidth={2} /> {pub.particular ? "Particular — voltar a compartilhar" : "Marcar como particular"}
              </Button>
            )
          )}
          {pub.status !== "cancelled" && (
            <Button
              data-testid="compartilhar-compromisso"
              size="sm"
              onClick={() => setShareOpen(true)}
              className="gap-2 border border-[#8cc7f5] bg-[#eaf5ff] text-[#0b4f94] hover:bg-[#dcefff] shadow-none"
            >
              <Share2 className="w-3.5 h-3.5" strokeWidth={2} /> Compartilhar
            </Button>
          )}
          {pub.status === "approved" && !pub.compartilhado && !new URLSearchParams(location.search).get("de") && (
            <Button asChild size="sm" variant="outline" className="gap-2 border-stone-300">
              <Link data-testid="compartilhar-parceiro" to={`/compartilhamentos?novo=evento&ref=${pub.pub_id}`} title="Dar a um parceiro externo acesso de consulta a este evento, com validade">
                <Users className="w-3.5 h-3.5" strokeWidth={2} /> Parceiro externo
              </Link>
            </Button>
          )}
          {pub.status !== "cancelled" && (
            <NewEventDialog
              existing={pub}
              onCreated={load}
              trigger={
                <Button
                  data-testid="edit-event-button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={2} /> Editar evento
                </Button>
              }
            />
          )}
          {pub.status === "cancelled" ? (
            <Button
              data-testid="reactivate-button"
              variant="outline"
              size="sm"
              onClick={reactivateHearing}
              disabled={reactivating}
              className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} /> {reactivating ? "Reativando…" : "Reativar"}
            </Button>
          ) : (
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="cancel-event-button"
                  variant="outline"
                  size="sm"
                  className="gap-2 border-red-200 text-red-700 hover:bg-red-100"
                >
                  <Ban className="w-3.5 h-3.5" strokeWidth={2} /> Cancelar
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancelar este evento?</DialogTitle>
                  <DialogDescription>
                    O evento correspondente é removido do Google Calendar. O registro continua
                    disponível para consulta, marcado como cancelado — dá para reativar depois
                    se necessário.
                  </DialogDescription>
                </DialogHeader>
                <div>
                  <label className="text-xs text-stone-500 block mb-1.5">Motivo do cancelamento (opcional)</label>
                  <Textarea
                    data-testid="cancel-reason-input"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Ex.: cancelada por decisão judicial, acordo entre as partes…"
                    rows={3}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCancelOpen(false)}>Voltar</Button>
                  <Button
                    data-testid="confirm-cancel-button"
                    onClick={cancelHearing}
                    disabled={cancelling}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {cancelling ? "Cancelando…" : "Confirmar cancelamento"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <header className={`mb-8 ${pub.status === "cancelled" ? "opacity-60" : ""}`}>
        <div className="flex items-center gap-3 mb-2">
          {pub.status === "cancelled" ? (
            <span
              data-testid="cancelled-badge"
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-stone-100 text-stone-500 border border-stone-200"
            >
              Cancelada pelo usuário
            </span>
          ) : (
            <EventTypeBadge hearing={h} />
          )}
          {h.event_type === "audiencia" && h.hearing_phase && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-[10px] font-bold uppercase tracking-wider text-stone-500">
              {PHASE_LABELS[h.hearing_phase] || h.hearing_phase}
            </span>
          )}
          {h.is_virtual && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              <Video className="w-3 h-3" strokeWidth={2} /> Videoconferência
            </span>
          )}
          <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand">
            Detalhe do evento
          </span>
        </div>
        <h1 className="font-display text-3xl font-bold text-stone-900">
          {h.parties || h.case_number || h.description || "Compromisso"}
        </h1>
        {h.parties && h.case_number && (
          <p className="text-sm text-stone-500 mt-1 tabular-nums"><ProcessoLink numero={h.case_number} /></p>
        )}
        {h.event_type === "prazo" && pub.status !== "cancelled" && (
          pub.prazo_done ? (
            <div data-testid="prazo-cumprido" className="mt-4 inline-flex flex-wrap items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
                <Check className="w-4 h-4" strokeWidth={2.25} />
                Prazo cumprido{pub.prazo_done_at ? ` em ${new Date(pub.prazo_done_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })}` : ""}
              </span>
              <Button data-testid="prazo-reabrir" size="sm" variant="outline" disabled={baixando} onClick={() => setPrazoDone(false)} className="h-8 gap-1.5 border-emerald-300 bg-white">
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} /> Desfazer
              </Button>
            </div>
          ) : (
            <div className="mt-4">
              {/* Lote 120: mesmo botão do Painel/Agenda — bolinha cinza com ✓ + "Marcar como feito" */}
              <MarcarFeito h={{ pub_id: id, prazo_done: false, hearing: { event_type: "prazo" } }} testid="prazo-feito" disabled={baixando} onToggleDone={() => setPrazoDone(true)} />
            </div>
          )
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-6">
          <div className={`bg-white border border-stone-200 rounded-md p-6 ${pub.status === "cancelled" ? "opacity-60" : ""}`}>
            <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-4">Dados</h2>
            <div className="grid grid-cols-2 gap-y-4 gap-x-8">
              <Info icon={Calendar} label="Data" value={formatDate(h.date)} />
              <Info icon={Clock} label="Horário" value={h.time} />
              {(h.party_author || h.party_defendant) ? (
                <>
                  <Info icon={User} label={(PARTY_LABELS[h.case_area] || PARTY_LABELS.outro)[0]} value={h.party_author} />
                  <Info icon={User} label={(PARTY_LABELS[h.case_area] || PARTY_LABELS.outro)[1]} value={h.party_defendant} />
                </>
              ) : (
                h.parties && (
                  <Info icon={User} label={h.event_type === "reuniao" ? "Participantes" : "Partes"} value={h.parties} span />
                )
              )}
              {h.event_type === "prazo" && h.deadline_type && (
                <Info label="Tipo de prazo" value={h.deadline_type} />
              )}
              {/* "Assunto" virou "Tipo" (Lote 56, pedido do Robson, 30/09/2026) — o tipo
                  "Reunião" virou "Outro" no seletor (perícia, diligência, reunião, etc.),
                  e esse mesmo campo (meeting_subject, sem mudar o nome internamente) passa
                  a servir pra especificar qual é o tipo, em vez de só um assunto de reunião. */}
              {h.event_type === "reuniao" && h.meeting_subject && (
                <Info label="Tipo" value={h.meeting_subject} span />
              )}
              {h.event_type === "reuniao" && h.meeting_address && (
                <Info icon={MapPin} label="Endereço" value={h.meeting_address} span />
              )}
              <Info icon={Building2} label="Vara / Comarca" value={formatCourtDistrict(h.court, h.court_district)} span />
              <Info icon={FileText} label="Processo" value={<ProcessoLink numero={h.case_number} />} span />
              {pub.shared_from && (
                <Info
                  icon={Users}
                  label="Compartilhado por"
                  value={`${pub.shared_from.from_name || "um colega"}${pub.shared_from.unlinked ? " (desvinculado)" : " — acompanha as mudanças dele"}`}
                  span
                />
              )}
              {h.event_type !== "reuniao" && publicationDate(pub) && (
                <Info icon={FileText} label="Data da publicação" value={formatDate(publicationDate(pub))} span />
              )}
              {h.description && <Info label="Descrição" value={h.description} span />}
              {pub.status === "cancelled" && pub.cancel_reason && (
                <Info label="Motivo do cancelamento" value={pub.cancel_reason} span />
              )}
            </div>
            {pub.calendar_link && (
              <a
                data-testid="open-calendar-link"
                href={pub.calendar_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 text-xs font-semibold text-emerald-brand hover:underline"
              >
                <CalendarPlus className="w-3.5 h-3.5" strokeWidth={2} /> Reabrir no Google Calendar
              </a>
            )}
          </div>

          {h.is_virtual && h.meeting_url && (
            <div data-testid="videoconf-section" className="bg-emerald-100 border border-emerald-200 rounded-md p-6">
              <div className="flex items-center gap-2 mb-3">
                <Video className="w-4 h-4 text-emerald-700" strokeWidth={2} />
                <span className="text-[11px] font-bold tracking-widest uppercase text-emerald-700">
                  {PROVIDER_LABEL[h.meeting_provider] || "Videoconferência"}
                </span>
              </div>
              <p className="text-sm text-emerald-700 mb-4 break-all">{h.meeting_url}</p>
              {h.meeting_id && (
                <div className="mb-2 text-sm text-emerald-700">
                  ID: <strong className="tabular-nums">{h.meeting_id}</strong>
                </div>
              )}
              {h.meeting_password && (
                <div className="mb-4 text-sm text-emerald-700">
                  Senha: <strong className="tabular-nums">{h.meeting_password}</strong>
                </div>
              )}
              <a
                data-testid="join-video-button"
                href={h.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-brand text-white text-sm font-semibold px-4 py-2 rounded-md"
              >
                <Video className="w-4 h-4" strokeWidth={2} /> Entrar na chamada
              </a>
            </div>
          )}

          {pub.raw_text && (
            <div className="bg-white border border-stone-200 rounded-md p-6">
              <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-3">
                {pub.source === "manual" ? "Anotações" : "Texto original"}
              </h2>
              <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{brDatesInText(pub.raw_text)}</p>
            </div>
          )}

          {client && (
            <div className="bg-white border border-stone-200 rounded-md p-6">
              <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-4">
                {pub.status === "cancelled" ? "Avisar cliente do cancelamento" : "Notificar cliente"}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-stone-500 block mb-1.5">Mensagem WhatsApp (rascunho)</label>
                  <Textarea
                    data-testid="whatsapp-message"
                    value={waMsg}
                    onChange={(e) => setWaMsg(e.target.value)}
                    rows={7}
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    data-testid="whatsapp-button"
                    onClick={logWhats}
                    disabled={loading}
                    className="bg-[#25D366] hover:bg-[#20b95a] text-white gap-2"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={2} /> Abrir WhatsApp e registrar
                  </Button>
                  <Button
                    data-testid="email-button"
                    onClick={sendEmail}
                    disabled={loading}
                    variant="outline"
                    className="gap-2"
                  >
                    <Send className="w-4 h-4" strokeWidth={2} /> Enviar e-mail agora
                  </Button>
                </div>
              </div>
            </div>
          )}

          {pub.status !== "cancelled" && (h.event_type || "audiencia") === "audiencia" && (
            <div data-testid="testemunhas-section" className="bg-white border border-stone-200 rounded-md p-6">
              <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-1 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-stone-400" strokeWidth={1.75} /> Testemunhas
              </h2>
              <Dica className="text-xs text-stone-500 mb-4">Convite pronto para enviar por WhatsApp ou e-mail, com link para a testemunha confirmar a presença.</Dica>
              {(pub.testemunhas || []).length > 0 && (
                <ul className="mb-4 divide-y divide-stone-100 border border-stone-200 rounded-md" data-testid="testemunhas-lista">
                  {pub.testemunhas.map((t) => (
                    <li key={t.tid} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5" data-testid={`testemunha-${t.tid}`}>
                      <span className="text-sm font-medium text-stone-900 min-w-0 break-words">{t.nome}</span>
                      <span
                        data-testid={`testemunha-status-${t.tid}`}
                        className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                          t.resposta === "confirmed" ? "bg-emerald-100 text-emerald-700"
                            : t.resposta === "cannot" ? "bg-amber-100 text-amber-700"
                            : t.ultimo_canal ? "bg-stone-100 text-stone-600" : "bg-stone-100 text-stone-500"}`}
                      >
                        {t.resposta === "confirmed" ? "Confirmou presença"
                          : t.resposta === "cannot" ? "Não poderá comparecer"
                          : t.ultimo_canal ? "Aguardando resposta" : "Convite não enviado"}
                      </span>
                      <span className="ml-auto flex gap-3 text-xs">
                        <button type="button" className="text-stone-700 underline" data-testid={`testemunha-reenviar-${t.tid}`}
                          onClick={() => { setTesAtual(t); setTesOpen(true); }}>Enviar de novo</button>
                        <button type="button" className="text-stone-500 underline" data-testid={`testemunha-remover-${t.tid}`}
                          onClick={async () => {
                            try { await http.delete(`/publications/${id}/testemunhas/${t.tid}`); toast.success("Testemunha removida"); load(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Erro ao remover"); }
                          }}>Remover</button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Button data-testid="testemunha-convidar-button" variant="outline" className="gap-2"
                onClick={() => { setTesAtual(null); setTesOpen(true); }}>
                <UserPlus className="w-4 h-4" strokeWidth={2} /> Convidar testemunha
              </Button>
            </div>
          )}

          <div id="avisar-equipe" data-testid="team-share-section" className="bg-white border border-stone-200 rounded-md p-6">
            <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-1 flex items-center gap-2">
              <Users className="w-4 h-4 text-stone-400" strokeWidth={1.75} /> Compartilhar este compromisso
            </h2>
            <Dica className="text-xs text-stone-500 mb-4">{`Avise a Equipe, um colega vinculado ou outra pessoa (mensagem no IApauta, e-mail ou WhatsApp). Por e-mail e WhatsApp vai o link para a pessoa ver os dados e confirmar que recebeu, sem precisar de login.`}</Dica>
            <Button
              data-testid="compartilhar-compromisso-rodape"
              onClick={() => setShareOpen(true)}
              disabled={pub.status === "cancelled"}
              className="gap-2 border border-[#8cc7f5] bg-[#eaf5ff] text-[#0b4f94] hover:bg-[#dcefff] shadow-none"
            >
              <Share2 className="w-4 h-4" strokeWidth={2} /> Compartilhar
            </Button>
          </div>
        </section>

        <aside className="space-y-6">
          {client ? (
            <div className="bg-white border border-stone-200 rounded-md p-6">
              <h3 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-4">
                Cliente vinculado
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-stone-400" strokeWidth={1.5} /> <strong>{client.name}</strong></div>
                <div className="flex items-center gap-2 text-stone-500"><Phone className="w-4 h-4 text-stone-400" strokeWidth={1.5} /> {client.phone}</div>
                <div className="flex items-center gap-2 text-stone-500"><Mail className="w-4 h-4 text-stone-400" strokeWidth={1.5} /> {client.email}</div>
              </div>
            </div>
          ) : (
            <div className="bg-amber-100 border border-amber-200 rounded-md p-6 text-sm text-amber-700">
              Nenhum cliente vinculado.
            </div>
          )}

          {confirmUrl && pub.status !== "cancelled" && (
            <div data-testid="confirm-link-card" className="bg-stone-50 border border-stone-200 rounded-md p-6">
              <h3 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-2">
                Convite para o cliente
              </h3>
              <p className="text-xs text-stone-500 mb-3 leading-relaxed">
                Link de um clique para o cliente confirmar presença ou pedir remarcação.
              </p>
              {pub.client_response ? (
                <div className={`mb-3 p-3 rounded-md text-xs ${pub.client_response === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {pub.client_response === "confirmed" ? (
                    <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={2} /> Cliente confirmou presença</span>
                  ) : (
                    <span className="flex items-center gap-1"><X className="w-3.5 h-3.5" strokeWidth={2} /> Cliente pediu remarcação</span>
                  )}
                  <div className="text-[10px] mt-0.5 opacity-80">
                    {new Date(pub.client_responded_at).toLocaleString("pt-BR")}
                  </div>
                </div>
              ) : (
                <div className="mb-3 p-2 rounded-md bg-stone-100 text-xs text-stone-500 text-center">
                  Aguardando resposta do cliente
                </div>
              )}
              <button
                data-testid="copy-confirm-link"
                onClick={copyConfirmLink}
                className="w-full text-xs font-semibold text-stone-700 hover:text-stone-900 flex items-center justify-center gap-1.5 py-2 border border-stone-300 rounded-md hover:bg-stone-100"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Copiar link
              </button>
            </div>
          )}

          {pub.team_share_token && (
            <div data-testid="team-status-card" className="bg-stone-50 border border-stone-200 rounded-md p-6">
              <h3 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-2">
                Aviso à equipe
              </h3>
              <p className="text-xs text-stone-500 mb-3 leading-relaxed">
                {pub.team_action}
                {pub.team_recipient_name ? ` — enviado a ${pub.team_recipient_name}` : ""}
              </p>
              {pub.team_ack_at ? (
                <div className="mb-3 p-3 rounded-md text-xs bg-emerald-100 text-emerald-700">
                  <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={2} /> {pub.team_ack_by} confirmou recebimento</span>
                  <div className="text-[10px] mt-0.5 opacity-80">
                    {new Date(pub.team_ack_at).toLocaleString("pt-BR")}
                  </div>
                </div>
              ) : (
                <div className="mb-3 p-2 rounded-md bg-stone-100 text-xs text-stone-500 text-center">
                  Aguardando confirmação de recebimento
                </div>
              )}
              <button
                data-testid="copy-team-link"
                onClick={copyTeamLink}
                className="w-full text-xs font-semibold text-stone-700 hover:text-stone-900 flex items-center justify-center gap-1.5 py-2 border border-stone-300 rounded-md hover:bg-stone-100"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Copiar link
              </button>
            </div>
          )}

          <div className="bg-white border border-stone-200 rounded-md p-6">
            <h3 className="font-display text-sm font-bold tracking-widest uppercase text-stone-500 mb-4">
              Histórico de avisos
            </h3>
            {history.length === 0 ? (
              <p className="text-xs text-stone-500">Nenhum aviso registrado ainda.</p>
            ) : (
              <ol className="space-y-4 relative before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-stone-300">
                {history.map((n) => {
                  const isResp = n.channel === "client_response";
                  const isPost = n.channel === "post_hearing_reminder";
                  const isValidated = n.channel === "validated";
                  const isCancelled = n.channel === "cancelled";
                  const isReactivated = n.channel === "reactivated";
                  const isTeamShare = n.channel === "team_share";
                  const isTeamAck = n.channel === "team_ack";
                  const isTesConvite = n.channel === "testemunha_convite";   // Lote 176
                  const isTesResp = n.channel === "testemunha_response";
                  const isAuto = (n.message || "").startsWith("[auto]");
                  const color = isResp
                    ? (n.status === "confirmed" ? "bg-emerald-brand" : "bg-amber-500")
                    : isTesResp ? (n.status === "confirmed" ? "bg-emerald-brand" : "bg-amber-500")
                    : isPost ? "bg-amber-500"
                    : isValidated ? "bg-emerald-brand"
                    : isCancelled ? "bg-red-500"
                    : isReactivated ? "bg-emerald-brand"
                    : isTeamAck ? "bg-emerald-brand"
                    : isTeamShare ? "bg-stone-900"
                    : "bg-stone-900";
                  return (
                    <li key={n.notification_id} className="relative pl-6" data-testid={`notif-${n.notification_id}`}>
                      <span className={`absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full ${color} flex items-center justify-center`}>
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                      <div className="text-xs font-semibold text-stone-900">
                        {isResp
                          ? n.status === "confirmed" ? "Cliente confirmou" : "Cliente pediu remarcação"
                          : isTesConvite ? "Convite de testemunha"
                          : isTesResp ? (n.status === "confirmed" ? "Testemunha confirmou" : "Testemunha não poderá comparecer")
                          : isPost ? "Lembrete pós-audiência: conferir ata"
                          : isValidated ? "Dados validados"
                          : isCancelled ? "Evento cancelado"
                          : isReactivated ? "Evento reativado"
                          : isTeamShare ? "Equipe avisada"
                          : isTeamAck ? "Equipe confirmou recebimento"
                          : n.channel === "email" ? (isAuto ? "E-mail automático" : "E-mail enviado")
                          : (isAuto ? "WhatsApp automático" : "WhatsApp registrado")}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {new Date(n.sent_at).toLocaleString("pt-BR")}
                      </div>
                      {isPost || isCancelled ? (
                        <div className={`text-xs mt-1 leading-relaxed rounded p-2 ${isCancelled ? "text-red-700 bg-red-100 border border-red-200" : "text-stone-700 bg-amber-100 border border-amber-200"}`}>
                          {n.message}
                        </div>
                      ) : isReactivated || isTeamAck ? null : isTeamShare || isTesConvite || isTesResp ? (
                        <div className="text-xs text-stone-500 mt-1 break-words">{n.message}</div>
                      ) : (
                        <div className="text-xs text-stone-500 mt-1 truncate">para {n.recipient}</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </aside>
      </div>
      <CompartilharPublicacao modo="compromisso" item={pub} open={shareOpen} onClose={() => setShareOpen(false)} />
      <ConvidarTestemunha pub={pub} open={tesOpen} testemunha={tesAtual} onClose={() => setTesOpen(false)} onChanged={load} />
    </div>
  );
}

const Info = ({ icon: Icon, label, value, span }) => (
  <div className={span ? "col-span-2" : ""}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500 mb-1">
      {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />} {label}
    </div>
    <div className="text-stone-900 font-medium">{value || "—"}</div>
  </div>
);
